import {Plural, Trans} from '@lingui/macro';
import _ from 'lodash';
import React, {FC, useState} from 'react';

import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {useAppDispatch, useAppSelector} from '../../../../helpers/reduxHelpers';
import {SignatureModel} from '../../../../models/api/signatureModel';
import {deleteSignaturesAsyncAction} from '../../../../store/data/signatures/signaturesActions';
import {getSignatures} from '../../../../store/data/signatures/signaturesSelectors';
import {SettingsBannersEnum, useSettingsBanners} from '../../../banners/settingsBannersContext';
import {SignaturesDeleteDialog} from '../../dialog/signaturesDeleteDialog';
import {SignaturesError} from '../../signaturesHelpers';

/*
 * Props.
 */

export interface SignaturesTableMultiSelectDeleteActionProps {
  signatureIds: ReadonlyArray<number>;
  onSuccessfulDelete: Handler;
}

/*
 * Component.
 */

export const SignaturesTableMultiSelectDeleteAction: FC<SignaturesTableMultiSelectDeleteActionProps> = (
  props,
) => {
  const {signatureIds, onSuccessfulDelete} = props;
  const [isShowingConfirmationDialog, setIsShowingConfirmationDialog] = useState(false);
  const dispatch = useAppDispatch();
  const {showBanner} = useSettingsBanners();
  const signaturesMap = useAppSelector(getSignatures);
  const signatures = _(signatureIds)
    .map((id) => signaturesMap[id])
    .compact()
    .value();

  const deleteSignatures = async () => {
    try {
      await dispatch(deleteSignaturesAsyncAction(signatureIds));
      showBanner({type: SettingsBannersEnum.SUCCESS, message: renderSuccessMessage(signatures)});
      onSuccessfulDelete();
    } catch (err) {
      if (err instanceof SignaturesError) {
        showBanner({
          type: SettingsBannersEnum.ERROR,
          message: err.message,
        });
      }
    }
  };

  return (
    <>
      <Button
        variant={ButtonVariantsEnum.SECONDARY_DANGER}
        isRounded
        onClick={() => setIsShowingConfirmationDialog(true)}
      >
        <Trans id="signatures-table-multi-select-delete">Delete</Trans>
      </Button>
      {isShowingConfirmationDialog && (
        <SignaturesDeleteDialog
          signatures={signatures}
          onClose={() => setIsShowingConfirmationDialog(false)}
          onDelete={deleteSignatures}
        />
      )}
    </>
  );
};

/*
 * Helpers.
 */

function renderSuccessMessage(signatures: ReadonlyArray<SignatureModel>) {
  return (
    <Plural
      id="delete-signatures-success"
      value={signatures.length}
      one={`Signature "${signatures[0].name}" has been deleted`}
      other="Deleted # signatures"
    />
  );
}
